/**
 * React Router Configuration
 * Defines all application routes
 */

import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import Layout from '@/components/common/Layout';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import LoginPage from '@/pages/LoginPage';
import LogoutPage from '@/pages/LogoutPage';
import BalancePage from '@/pages/BalancePage';
import ConfirmationPage from '@/pages/ConfirmationPage';
import TransactionResultPage from '@/pages/TransactionResultPage';
import ErrorPage from '@/pages/ErrorPage';
import NotFoundPage from '@/pages/NotFoundPage';
import { ROUTE_PATHS } from '@/utils/constants';

function RouteErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div className="max-w-lg mx-auto mt-12 p-6 bg-surface border border-border rounded-lg text-center">
      <h2 className="text-xl font-semibold text-danger mb-2">Something went wrong</h2>
      <p className="text-sm text-text-muted mb-4">{error.message}</p>
      <button
        onClick={resetErrorBoundary}
        className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors"
      >
        Try Again
      </button>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ErrorBoundary FallbackComponent={RouteErrorFallback}>
        <Layout />
      </ErrorBoundary>
    ),
    errorElement: (
      <Layout>
        <ErrorPage />
      </Layout>
    ),
    children: [
      {
        // Root redirects to login
        index: true,
        element: <Navigate to={ROUTE_PATHS.LOGIN} replace />,
      },
      {
        // Login page (public)
        path: ROUTE_PATHS.LOGIN,
        element: (
          <ErrorBoundary FallbackComponent={RouteErrorFallback}>
            <LoginPage />
          </ErrorBoundary>
        ),
      },
      {
        // Logout page (public) - 3rd-party redirect target
        path: ROUTE_PATHS.LOGOUT,
        element: (
          <ErrorBoundary FallbackComponent={RouteErrorFallback}>
            <LogoutPage />
          </ErrorBoundary>
        ),
      },
      {
        // Balance page (protected)
        path: ROUTE_PATHS.BALANCE,
        element: (
          <ErrorBoundary FallbackComponent={RouteErrorFallback}>
            <ProtectedRoute>
              <BalancePage />
            </ProtectedRoute>
          </ErrorBoundary>
        ),
      },
      {
        // Confirmation page (protected)
        path: ROUTE_PATHS.CONFIRMATION,
        element: (
          <ErrorBoundary FallbackComponent={RouteErrorFallback}>
            <ProtectedRoute>
              <ConfirmationPage />
            </ProtectedRoute>
          </ErrorBoundary>
        ),
      },
      {
        // Transaction result page (public - uses token)
        path: ROUTE_PATHS.TRANSACTION_RESULT,
        element: (
          <ErrorBoundary FallbackComponent={RouteErrorFallback}>
            <TransactionResultPage />
          </ErrorBoundary>
        ),
      },
      {
        // Error page (public)
        path: ROUTE_PATHS.ERROR,
        element: <ErrorPage />,
      },
      {
        // 404 catch-all
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
